<template>
  <!-- eslint-disable max-len -->
  <router-link
    tag="div"
    :to="computedLink"
    class="app"
    :class="{
      app_store: isInStore,
      'app_no-link': !computedLink
    }"
  >
    <template>
      <v-lazy-image
        v-if="icon && !iconFailedToLoad"
        class="app__icon"
        :src="icon"
        :intersection-options="{ rootMargin: '500px' }"
        @error="iconFailedToLoad = true"
        @load="iconLoaded = true"
      />
      <div
        v-else
        class="app__empty-icon"
      ></div>
    </template>
    <div class="app__desc">
      <span class="app__title">
        {{ app.name }} <!--<span v-if="isInStore">({{ app.bundle_id }})</span>
        <span
          v-if="app.original_trackid && app.original_trackid !== '0'"
          class="app__badge"
          >{{ $t("TWEAKED") }}</span
        >-->
      </span>
      <div v-if="!isUpdatable" class="app__info">
        <span v-if="(app.version || app.bundle_version)" class="app__version"
          >v {{ app.version || app.bundle_version }}</span
        >
        <span v-if="app.size" class="app__size">{{
          prettysize(app.size)
        }}</span>
        <span v-if="isInStore" class="app__public">
          {{ app.is_public ? $t('PUBLIC') : $t('PRIVATE') }}
        </span>
        <!--<span v-if="app.price" class="app__price"
          ><img class="app__price-icon" src="@/assets/icon-price.svg" />
          {{ $t(app.price.toUpperCase()) }}</span
        >-->
        <router-link
          v-if="!isInStore"
          class="app__category"
          :to="`/search/?type=${type}&genre=${app.genre_id}`"
        >{{
            app.genre_id &&
            genres[type][app.genre_id] &&
            $t(genres[type][app.genre_id].name.toUpperCase())
          }}</router-link
        >
      </div>
      <router-link
        v-if="!isRequestable && !isUpdatable && !isInStore && +app.artist_id"
        class="app__publisher"
        :to="`/search/?type=${type}&dev=${app.artist_id}`"
        >{{ app.pname }}</router-link
      >
      <p v-if="isRequestable" class="app__status">
        {{ $t([app.status.toUpperCase(), app.status]) }} {{ $t("SINCE") }}
        {{ formatDate(app.status_changed_at * 1000) }}
      </p>
      <span v-if="isUpdatable" class="app__update">
        v {{ app.version_old
        }}<img class="app__update-icon" src="@/assets/icon-arrow.svg" />v
        {{ app.version_new }}
      </span>
      <span v-if="isUpdatable && !app.updateable" class="app__update-warning">{{
        $t("NOT_UPDATEABLE")
      }}</span>
      <span
        v-if="compatibilityString"
      >
        {{ compatibilityString }}
      </span>
      <div v-if="isInStore && app.detected_malware" class="app__notice app__notice_danger">
        <p>{{ $t('MALWARE_DETECTED_NOTICE') }}</p>
        <p v-for="malware of app.detected_malware" :key="malware.name">
          {{ malware.name }}, {{ malware.type }}. {{ $t('LEVEL') }}:&nbsp;{{ malware.level }}. {{ $t('DANGER') }}:&nbsp;{{ malware.danger }}
        </p>
      </div>
    </div>
    <div
      v-if="isRequestable"
      class="app__request request"
    >
      <v-button
        v-if="isDeviceLinked && app.status !== 'fulfilled'"
        type="primary"
        @click.capture.stop.native="$emit('tryFulfill', app)"
      >
        {{ $t('FULFILL') }}
      </v-button>
    </div>
    <div v-if="isInStore" class="actions">
      <button
        @click="installIpa"
        class="actions__install"
        :class="{ actions__install_disabled: installingIpa }"
      >
        <img
          class="actions__install-icon"
          :src="require(`@/assets/themes/${BRAND_KEY}/images/icon-install-primary.svg`)"
        />
        {{ $t("INSTALL") }}
        <template v-if="installingIpa">
          <div class="actions__loader">
            <div class="actions__loader-dot"></div>
            <div class="actions__loader-dot"></div>
            <div class="actions__loader-dot"></div>
          </div>
          <div class="actions__tooltip">{{ $t("REQUESTING") }}...</div>
        </template>
      </button>
      <router-link :to="`/details/${app.universal_object_identifier}?id=${app.id}&editMode`">{{ $t('EDIT_METADATA') }}</router-link>
      <button @click.prevent="deleteIpa" class="actions__remove">
        {{ deletingIpa ? $t("APP_REMOVING") : $t("REMOVE") }}
      </button>
    </div>
  </router-link>
</template>

<script>
import prettysize from 'prettysize';
import installAppMixin from '@/mixins/installApp';
import VLazyImage from 'v-lazy-image/v2';
import { operatingSystems } from '@/data';
import VButton from '@/components/Button.vue';
import { mapGetters } from 'vuex';

export default {
  mixins: [installAppMixin],
  components: {
    VLazyImage,
    VButton,
  },
  props: {
    isRequestable: Boolean,
    isUpdatable: Boolean,
    isInStore: Boolean,
    isInRepo: Boolean,
    app: Object,
    type: String,
    repoId: String,
    index: Number,
    link: String,
  },
  data() {
    return {
      deletingIpa: false,
      installingIpa: false,
      iconFailedToLoad: false,
      iconLoaded: false,
    };
  },
  computed: {
    ...mapGetters(['isDeviceLinked']),
    computedLink() {
      if (this.link) {
        return this.link;
      }

      if (this.isInStore) {
        return '';
      }

      if (this.isRequestable) {
        return this.app.status === 'fulfilled' ? `/details/${this.app.fulfilled_universal_object_identifier}` : '';
      }

      return `/app/${(this.type || this.app.type)}/${(this.isRequestable || this.isUpdatable) ? this.app.trackid : this.app.id}`;
    },
    genres() {
      return this.$store.getters.genres;
    },
    compatibilityString() {
      const devices = operatingSystems.filter(({ name }) => this.app[`min_${name}_version`]).map(({ device }) => device);

      if (!devices.length) {
        return '';
      }

      return this.$t('FOR_X', [devices.join(', ')]);
    },
    icon() {
      return this.app.image || this.app.iconURL || this.app.icon_uri;
    },
  },
  methods: {
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
    deleteIpa() {
      if (!this.isInStore || this.deletingIpa) {
        return;
      }

      this.$confirm(this.$t('DELETE_APP_WARNING')).then(() => {
        this.deletingIpa = true;
        this.$api.deleteIpa(this.app.id).then(() => {
          this.$emit('deleted');
        });
      });
    },
    installIpa() {
      if (this.installingIpa) {
        return;
      }

      if (this.app.no_installation_ticket_failure_reason) {
        this.$notify({
          type: 'error',
          text: this.app.no_installation_ticket_failure_reason.translated,
        });
        return;
      }

      this.installingIpa = true;

      this.installApp(this.app.id, 'universal', { installationTicket: this.app.installation_ticket }).finally(() => { this.installingIpa = false; });
    },
    prettysize,
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.app {
  padding: 10px;
  display: flex;
  border: 1px solid transparent;
  cursor: pointer;

  &_no-link {
    cursor: default;
  }

  &_store {
    padding: 0;
  }

  &_store &__info {
    margin-bottom: 10px;
  }

  @include notebook {
    padding: 0;
    border: none;

    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }

  &:not(&_store):hover {
    @include desktop-only {
      border: 1px solid var(--tertiary-accent);
      box-shadow: 0px 4px 10px 0 var(--shadow-color);
      border-radius: 8px;
    }
  }

  &__desc {
    display: flex;
    flex-direction: column;
  }

  &__icon,
  &__empty-icon {
    flex: 0 0 100px;
    height: 100px;
    width: 100px;
    border-radius: 12px;
    margin-right: 15px;

    &:not(&_vertical) {
      border-radius: 22.5%;
      mask: url(../assets/ios-mask.svg) center/100% 100% no-repeat;
    }

    @media (max-width: 767px) {
      width: 50px;
      height: 50px;
      flex: 0 0 50px;
    }

    &_vertical {
      height: 150px;

      @media (max-width: 767px) {
        height: 75px;
      }
    }
  }

  &__empty-icon {
    background-color: var(--stub-bg);
  }

  &__icon {
    object-fit: cover;
    object-position: center;

    &_loading {
      opacity: 0;
    }
  }

  &__title {
    color: var(--text-tertiary);
    margin-bottom: 6px;
    margin-top: 5px;
    display: block;
    line-height: 20px;
    word-break: break-word;

    @include tablet {
      margin-top: 0;
    }
  }

  &__badge {
    font-size: 10px;
    padding: 4px 3px 3px 3px;
    color: #2b2b05;
    text-transform: uppercase;
    border-radius: 2px;
    background-color: var(--bg-warning);
    margin-left: 3px;
    position: relative;
    top: -1px;
  }

  &__info,
  &__category,
  &__publisher {
    color: var(--text-primary);
    font-weight: 400;
  }

  &__version,
  &__price,
  &__size {
    display: inline-block;
    margin-right: 30px;

    @media (max-width: 767px) {
      margin-right: 8px;
    }
  }

  &__category,
  &__publisher {
    text-decoration: underline dotted;
  }

  &__price-icon {
    position: relative;
    top: 1px;
    filter: var(--img-filter);
  }

  &__publisher {
    display: inline-block;
    margin-top: 12px;
  }

  &__status {
    margin-top: auto;
    color: #525d66;
    font-size: 12px;
    padding: 10px 0 3px;
    font-weight: 400;
  }

  &__request {
    margin-left: auto;

    @include notebook {
      padding-left: 5px;
    }
  }

  &__update {
    color: var(--text-primary);
    font-weight: 400;
    margin-bottom: 7px;

    &-icon {
      margin: 0 30px;
    }

    &-warning {
      color: var(--configure-error-text);
      background: var(--bg-error);
      border-radius: 2px;
      padding: 3px;
      font-weight: 400;
      margin-top: auto;
      font-size: 12px;
    }
  }

  &__store-warning {
    background: var(--bg-warning);
    border-radius: 2px;
    color: #424225;
    padding: 3px;
    font-weight: 400;
    margin-top: auto;

    &_success {
      background: var(--bg-success);
      color: #053305;
    }

    &_error {
      background: var(--bg-error);
      color: var(--text-primary);
    }
  }

  &__notice {
    margin-top: 10px;
    word-break: break-word;

    &_danger {
      color: var(--text-error);
    }

    p:not(:first-child) {
      margin-top: 5px;
    }
  }
}

.request {
  text-align: right;

  &_active &__button {
    color: #fff;
    background: #d75b5b;
    box-shadow: none;
  }

  &_active &__icon-fill {
    fill: #b06a6a;
  }

  &__info {
    padding: 4px;
    background: var(--request-button-bg);
    border-radius: 2px;
    display: inline-flex;
    align-items: center;

    @include notebook {
      margin-right: 5px;
    }
  }

  &__button {
    background: var(--bg);
    border: 1px solid rgba(215, 91, 91, 0.5);
    border-radius: 2px;
    padding: 2px 8px;
    font-size: 12px;
    color: var(--text-primary);
    @include transition;
  }

  &__icon {
    width: 14px;
    height: 9px;
    margin: 0 2px 0 4px;

    &-fill {
      @include transition;
    }
  }

  &__num {
    color: var(--text-primary);
    font-size: 12px;
  }

  &__text {
    display: flex;
    align-items: center;
    color: var(--text-primary);
    font-size: 12px;
    margin-top: 4px;

    @include notebook {
      display: none;
    }
  }

  &__plus {
    background: var(--request-plus-bg);
    border-radius: 1.25px;
    padding: 3px 5px;
    margin-right: 4px;
    font-size: 8px;
  }
}

.actions {
  margin-left: auto;
  padding-left: 15px;
  padding-top: 7px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  white-space: nowrap;

  @include tablet {
    padding-top: 0;
  }

  &__install {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    font-size: 14px;
    padding: 4px 10px;
    color: $primary;
    font-weight: 500;

    &-icon {
      margin-right: 3px;
    }

    &_disabled {
      pointer-events: none;
    }
  }

  &__remove {
    color: #fa6565;
    font-size: 14px;
  }

  &__loader {
    display: flex;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: var(--bg);
    align-items: center;
    justify-content: center;

    &-dot {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      margin-right: 3px;
      background: var(--text-primary);
      animation: wave 1.3s linear infinite;

      &:nth-child(2) {
        animation-delay: -1.1s;
      }

      &:nth-child(3) {
        animation-delay: -0.9s;
      }
    }

    @keyframes wave {
      0%,
      60%,
      100% {
        transform: initial;
      }

      30% {
        transform: translateY(-4px);
      }
    }
  }

  &__tooltip {
    position: absolute;
    left: -1px;
    top: -25px;
    color: #ffffff;
    font-size: 12px;
    background: #4e4e4e;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.12);
    border-radius: 2px;
    white-space: nowrap;
    font-weight: 400;
    padding: 3px 5px;

    &:before {
      content: "";
      display: block;
      position: absolute;
      border-style: solid;
      border-width: 5px 4.5px 0 4.5px;
      border-color: #4e4e4e transparent transparent transparent;
      bottom: -5px;
      left: 15px;
    }
  }
}
</style>
